<template>
  <v-dialog v-model="open" width="700" max-width="100%" persistent>
    <v-card
      class="mx-auto"
      :class="{
        'w-popup-dialog': true,
        'w-popup-dialog--mobile': $voicer.isMobile,
      }"
    >
      <v-toolbar flat class="grey lighten-4">
        <v-spacer />
        <v-btn icon small @click.stop="open = false">
          <v-icon color="accent darken-4">mdi-close-thick</v-icon>
        </v-btn>
      </v-toolbar>
      <template v-for="(item, index) in getOrderedElements()">
        <v-img
          v-if="
            item.type === 'image' &&
            popupData &&
            popupData.metadatas &&
            currentImages &&
            currentImages[item.index]
          "
          :key="'element-' + index"
          :src="currentImages[item.index].value"
          :style="popupData.metadatas.image_click ? 'cursor: pointer' : ''"
          @click="handleClick(popupData.metadatas.image_click)"
        />
        <v-card-text
          v-if="item.type === 'content' && popupData && popupData.content"
          :key="'element-' + index"
        >
          <div class="text-body-1" v-html="$md.render(popupData.content)"></div>
        </v-card-text>
        <v-card-actions
          v-if="
            item.type === 'cta' &&
            popupData &&
            popupData.metadatas &&
            popupData.metadatas.cta &&
            popupData.metadatas.cta[item.index]
          "
          :key="'element-' + index"
          class="justify-center pb-4"
        >
          <v-btn
            color="primary"
            @click="
              handleClick(popupData.metadatas.cta[item.index].cta_action_url)
            "
          >
            <v-icon v-if="popupData.metadatas.cta[item.index].cta_icon" left
              >mdi-{{ popupData.metadatas.cta[item.index].cta_icon }}</v-icon
            >
            {{ popupData.metadatas.cta[item.index].cta_label }}
          </v-btn>
        </v-card-actions>
      </template>
    </v-card>
  </v-dialog>
</template>
<script>
import localstore from 'store'
import spoke from '~/utils/spoke'

export default {
  data() {
    return {
      open: false,
      popupData: null,
      loading: false,
      intervalId: null,
      frequency: 'once',
      customMinutes: null,
    }
  },

  computed: {
    currentImages() {
      if (!this.popupData || !this.popupData.metadatas) {
        return []
      }

      const metadatas = this.popupData.metadatas

      // Mobile preferred
      if (this.$voicer.isMobile && metadatas.image_mobile) {
        return metadatas.image_mobile
      }

      // Desktop preferred
      if (metadatas.image_desktop) {
        return metadatas.image_desktop
      }

      // Fallback: generic key
      return metadatas.image || []
    },
  },

  watch: {
    open(status) {
      if (status === false) {
        this.$store.dispatch('player/openPersistantPlayer')
        document.body.style.overflow = ''
        this.futurPopup(this.frequency, this.customMinutes)
      } else {
        document.body.style.overflow = 'hidden'
      }
    },
  },

  beforeMount() {
    this.fetchPopupData()
  },

  beforeDestroy() {
    if (this.intervalId) {
      clearInterval(this.intervalId)
      this.intervalId = null
    }
  },

  methods: {
    getStorageKey() {
      return `popup_dialog_last_shown_${this.popupData.id}`
    },

    getLastShown() {
      return localstore.get(this.getStorageKey())
    },

    openPopup() {
      this.open = true
      localstore.set(this.getStorageKey(), new Date())
      this.$store.dispatch('player/closePersistantPlayer')
    },

    futurPopup(frequency, customMinutes) {
      if (this.intervalId) {
        clearInterval(this.intervalId)
        this.intervalId = null
      }

      if (frequency === 'always' || frequency === 'once') {
        return
      }

      if (frequency === 'daily') {
        const lastShown = this.getLastShown()
        if (!lastShown) return

        const now = new Date()
        const nextShowTime = new Date(lastShown)
        nextShowTime.setHours(nextShowTime.getHours() + 24)

        const timeUntilNext = nextShowTime - now

        if (timeUntilNext > 0) {
          this.intervalId = setInterval(() => {
            if (!this.open) {
              this.openPopup()
            }
          }, timeUntilNext)
        }
      }

      if (frequency === 'custom' && customMinutes) {
        const customInMs = parseInt(customMinutes) * 60 * 1000
        this.intervalId = setInterval(() => {
          if (!this.open) {
            this.openPopup()
          }
        }, customInMs)
      }
    },

    shouldShowPopup(frequency, customMinutes) {
      const lastShown = this.getLastShown()

      if (frequency === 'always') {
        return true
      }

      if (frequency === 'once') {
        return !lastShown
      }

      if (frequency === 'daily') {
        if (!lastShown) return true
        const now = new Date()
        const lastDate = new Date(lastShown)
        const diffMs = now - lastDate
        const hoursDiff = diffMs / (1000 * 60 * 60)
        return hoursDiff >= 24
      }

      if (frequency === 'custom' && customMinutes) {
        if (!lastShown) return true

        const now = new Date()
        const customTimeLater = new Date(lastShown)
        customTimeLater.setMinutes(
          customTimeLater.getMinutes() + parseInt(customMinutes)
        )
        return now >= customTimeLater
      }

      return false
    },
    async fetchPopupData() {
      try {
        this.loading = true
        const params = {
          is: 'popup',
          limit: 1,
        }

        const response = await spoke.http.get(`/contents`, { params })

        if (response && response.items && response.items.length > 0) {
          this.popupData = response.items[0]

          this.frequency = 'once'
          this.customMinutes = null

          if (this.popupData.metadatas && this.popupData.metadatas.frequency) {
            this.frequency = this.popupData.metadatas.frequency
          }

          if (
            this.popupData.metadatas &&
            this.popupData.metadatas['custom-minutes']
          ) {
            this.customMinutes = this.popupData.metadatas['custom-minutes']
          }

          if (this.frequency === 'custom' && !this.customMinutes) {
            this.frequency = 'once'
          }

          if (
            this.shouldShowPopup(this.frequency, this.customMinutes) &&
            !this.open
          ) {
            this.openPopup()
          }
        }
        this.loading = false
      } catch (error) {
        this.loading = false
      }
    },
    handleClick(url) {
      if (url) {
        const newWin = window.open(url, '_blank')
        if (newWin) newWin.opener = null
      }
      this.open = false
    },
    getOrderedElements() {
      if (!this.popupData) {
        return []
      }
      const defaultOrder = ['image-0', 'name', 'content', 'cta-0']
      let order = defaultOrder

      if (this.popupData.metadatas && this.popupData.metadatas.order) {
        order = this.popupData.metadatas.order
      }

      const elements = []
      for (let i = 0; i < order.length; i++) {
        const item = order[i]
        const parts = item.split('-')
        const type = parts[0]
        let index = null

        if (parts.length > 1) {
          index = parseInt(parts[1])
        }

        elements.push({
          type,
          index,
          original: item,
        })
      }
      return elements
    },
  },
}
</script>
<style lang="scss" scoped>
.w-popup-dialog {
  &--mobile {
    display: flex;
    flex-direction: column;
    .v-card__text {
      flex-grow: 1;
      position: relative;
    }
  }
}
</style>
